import {
  ArrowUpRight,
  Building2,
  HeartPulse,
  ShoppingBag,
  Factory,
  Truck,
} from "lucide-react";
import { Link } from "react-router-dom";

const INDUSTRIES = [
  {
    icon: Building2,
    name: "Commercial Real Estate",
    body: "Streamline property management, tenant admin, and back-office workflows eating up your team's time.",
  },
  {
    icon: HeartPulse,
    name: "Healthcare",
    body: "Automate RCM, intake, and office admin. HIPAA-compliant by design, clinical workflows untouched.",
  },
  {
    icon: ShoppingBag,
    name: "E-commerce",
    body: "Offload customer support, returns, and product content. Built to flex with peak season.",
  },
  {
    icon: Factory,
    name: "Manufacturing",
    body: "Automate scheduling, supplier coordination, and quality inspection. Extends your ERP.",
  },
  {
    icon: Truck,
    name: "Logistics",
    body: "Run routing, carrier coordination, and document processing. Works alongside your TMS.",
  },
] as const;

const Industries = () => {
  return (
    <section id="industries" className="pt-8 pb-16 lg:pt-12 lg:pb-24 bg-secondary">
      <div className="section-container">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <span className="text-sm font-semibold uppercase tracking-wider text-gold mb-4 block">
            Industries
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight mb-5">
            Where AI Pays Off{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Fastest</span>
              <span className="absolute bottom-1 left-0 right-0 h-3 bg-gold/25 -z-0" />
            </span>
          </h2>
          <p className="text-lg text-body-gray">
            Five sectors where the opportunity is widest, and the savings come
            easiest.
          </p>
        </div>

        {/* Industry cards — 3 + 3 grid (5 industries + catch-all) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {INDUSTRIES.map((industry) => {
            const Icon = industry.icon;
            return (
              <div
                key={industry.name}
                className="group relative bg-background rounded-xl p-7 lg:p-8 border border-border/60 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                {/* Gold accent bar — left side, grows on hover */}
                <span className="absolute left-0 top-7 lg:top-8 bottom-7 lg:bottom-8 w-[3px] bg-gold/0 group-hover:bg-gold transition-colors duration-300 rounded-r" />

                {/* Icon */}
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-navy/5 group-hover:bg-gold/15 transition-colors mb-5">
                  <Icon className="w-5 h-5 text-navy" />
                </div>

                {/* Name */}
                <h3 className="text-xl lg:text-2xl font-serif text-navy mb-3 leading-tight">
                  {industry.name}
                </h3>

                {/* Body */}
                <p className="text-body-gray leading-relaxed text-[15px]">
                  {industry.body}
                </p>
              </div>
            );
          })}

          {/* Catch-all card — visually distinct (dashed border, muted) */}
          <Link
            to="/get-started"
            className="group relative bg-background/60 rounded-xl p-7 lg:p-8 border-2 border-dashed border-border hover:border-gold/50 hover:bg-background transition-all duration-300 flex flex-col justify-between min-h-[200px]">
            <div>
              <h3 className="text-xl lg:text-2xl font-serif text-navy mb-3 leading-tight">
                Don't see your industry?
              </h3>
              <p className="text-body-gray leading-relaxed text-[15px]">
                We work with $5M+ businesses across most operations-heavy
                sectors.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 text-gold font-semibold mt-5 group-hover:gap-3 transition-all">
              Talk to us
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Industries;
